import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { finalize, of } from 'rxjs';
import { MaterialModule } from '../material.module';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { YearlyCalibrationCertificateFormDialogComponent } from './yearly-calibration-certificate-form-dialog.component';
import { YearlyCalibrationCertificate } from './yearly-calibration-certificates.models';
import { YearlyCalibrationCertificatesService } from './yearly-calibration-certificates.service';

@Component({
  selector: 'app-yearly-calibration-certificate-details',
  standalone: true,
  imports: [CommonModule, MaterialModule, RouterModule],
  templateUrl: './yearly-calibration-certificate-details.component.html',
  styleUrl: './yearly-calibration-certificate-details.component.scss',
})
export class YearlyCalibrationCertificateDetailsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly service = inject(YearlyCalibrationCertificatesService);
  private readonly sanitizer = inject(DomSanitizer);

  certificate: YearlyCalibrationCertificate | null = null;
  pdfPreviewUrl: SafeResourceUrl | null = null;
  imagePreviewUrl: SafeUrl | null = null;
  previewErrorMessage = '';
  isPreviewLoading = false;
  private previewBlob: Blob | null = null;
  private previewObjectUrl: string | null = null;
  isLoading = true;
  isDownloading = false;
  isDeleting = false;
  errorMessage = '';

  ngOnInit(): void {
    this.loadCertificate();
  }

  ngOnDestroy(): void {
    this.clearPreview();
  }

  get canRetryPreview(): boolean {
    return this.certificate !== null && this.isPreviewable(this.certificate.contentType);
  }

  loadCertificate(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isSafeInteger(id) || id <= 0) {
      this.errorMessage = 'The certificate ID is invalid.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.service.getCertificate(id).pipe(
      finalize(() => this.isLoading = false),
    ).subscribe({
      next: certificate => {
        this.certificate = certificate;
        this.loadPreview(certificate);
      },
      error: () => this.errorMessage = 'The yearly calibration certificate could not be loaded. Please retry.',
    });
  }

  goBack(): void {
    void this.router.navigate(['/yearly-calibration-certificates']);
  }

  editCertificate(): void {
    if (!this.certificate) return;
    this.dialog.open(YearlyCalibrationCertificateFormDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: { certificate: this.certificate },
    }).afterClosed().subscribe(saved => {
      if (saved) {
        this.certificate = saved;
        this.loadPreview(saved);
      }
    });
  }

  downloadAttachment(): void {
    if (!this.certificate || this.isDownloading) return;
    const certificate = this.certificate;
    const download = this.previewBlob
      ? of(this.previewBlob)
      : this.service.downloadAttachment(certificate.id);
    this.isDownloading = true;
    download.pipe(
      finalize(() => this.isDownloading = false),
    ).subscribe({
      next: blob => this.saveBlob(blob, certificate.originalFileName),
      error: () => this.errorMessage = 'The certificate attachment could not be downloaded. Please retry.',
    });
  }

  retryPreview(): void {
    if (this.certificate) this.loadPreview(this.certificate);
  }

  deleteCertificate(): void {
    if (!this.certificate || this.isDeleting) return;
    const certificate = this.certificate;
    this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: {
        title: 'Delete Yearly Calibration Certificate',
        message: `Permanently delete "${certificate.name}" and its attached file? This action cannot be undone.`,
        confirmLabel: 'Delete Certificate',
        cancelLabel: 'Cancel',
        variant: 'warning',
      },
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.isDeleting = true;
      this.service.deleteCertificate(certificate.id).pipe(
        finalize(() => this.isDeleting = false),
      ).subscribe({
        next: () => this.goBack(),
        error: () => this.errorMessage = 'The yearly calibration certificate could not be deleted. Please retry.',
      });
    });
  }

  private loadPreview(certificate: YearlyCalibrationCertificate): void {
    this.clearPreview();
    if (!this.isPreviewable(certificate.contentType)) {
      this.previewErrorMessage = 'Inline preview is available for PDF, JPG/JPEG, and PNG files.';
      return;
    }

    this.isPreviewLoading = true;
    this.service.downloadAttachment(certificate.id).pipe(
      finalize(() => this.isPreviewLoading = false),
    ).subscribe({
      next: blob => {
        if (blob.size === 0) {
          this.previewErrorMessage = 'The attachment is empty and cannot be previewed.';
          return;
        }

        this.previewBlob = blob;
        this.previewObjectUrl = URL.createObjectURL(blob);
        if (certificate.contentType === 'application/pdf') {
          this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewObjectUrl);
        } else {
          this.imagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(this.previewObjectUrl);
        }
      },
      error: () => this.previewErrorMessage = 'The preview could not be loaded. You can still download the attachment.',
    });
  }

  private isPreviewable(contentType: string): boolean {
    return contentType === 'application/pdf'
      || contentType === 'image/jpeg'
      || contentType === 'image/png';
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private clearPreview(): void {
    if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
    this.previewObjectUrl = null;
    this.previewBlob = null;
    this.pdfPreviewUrl = null;
    this.imagePreviewUrl = null;
    this.previewErrorMessage = '';
  }
}
